import { useMutation, useQuery } from '@apollo/client';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Checkbox, InputBase, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate } from '@tanstack/react-router';
import { type FormikHelpers, useFormik } from 'formik';
import React, { type ChangeEvent, useEffect, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { AddButton, AddUsersModal, Card, Table, Title } from 'src/Administration/Common';
import type { Profile as AddUsersProfile } from 'src/Administration/Common/AddUsersModal/AddUsersModal';
import allMediagroups from 'src/api/permission/allMediaGroups';
import newMediaPermission from 'src/api/permission/newMediaPermission';
import newPermissionGroup from 'src/api/permission/newPermissionGroup';
import { useCustomToast } from 'src/hooks/menu';
import * as Yup from 'yup';
import { AdminTextInput } from '../Common/input';
import styles from './NewPermisionGroupPageStyles';

const GRID_CONTAINER_SPACING = 4;

interface HeadCell {
  id: string;
  numeric: boolean;
  disablePadding: boolean;
  label: string;
}

interface MediaGroup {
  id: string;
  name: string;
}

interface MediaGroupNode {
  node: MediaGroup;
}

interface AllMediaGroupsData {
  allMediaGroups: {
    edges: MediaGroupNode[];
  };
}

type Profile = AddUsersProfile;

interface UserListItem extends Profile {
  canEdit?: boolean;
  canDelete?: boolean;
  canDownload?: boolean;
  canShare?: boolean;
  hires?: boolean;
}

interface TableCellValue {
  value: React.ReactNode;
  display: boolean;
}

type TableRow = TableCellValue[];

interface FormValues {
  name: string;
  description: string;
}

interface CreatePermissionGroupResponse {
  createPermissionsGroup: {
    permissionsGroup: {
      id: string;
    };
  };
}

const headCellsUsersList: HeadCell[] = [
  { id: 'Username', numeric: true, disablePadding: false, label: 'Username' },
  { id: 'canEdit', numeric: true, disablePadding: false, label: 'Edit' },
  { id: 'canShare', numeric: true, disablePadding: false, label: 'Share' },
  { id: 'canDownload', numeric: true, disablePadding: false, label: 'Download' },
  { id: 'canDelete', numeric: true, disablePadding: false, label: 'Delete' },
  { id: 'hires', numeric: true, disablePadding: false, label: 'Request Hires' },
  { id: 'description', numeric: true, disablePadding: false, label: '' },
];

const generateCheckBoxes = (
  onChangeCan: (fieldIndex: number, user: UserListItem, event: ChangeEvent<HTMLInputElement>) => void,
  a: UserListItem,
): TableCellValue[] => {
  return Array(5)
    .fill(undefined)
    .map((_, e) => ({
      display: true,
      value: (
        <Checkbox
          sx={{ color: 'text.primary', '&.Mui-checked': { color: 'primary.main' } }}
          onChange={(event) => onChangeCan(e, a, event)}
        />
      ),
    }));
};

function convertUsersRows(
  data: UserListItem[],
  onChangeCan: (fieldIndex: number, user: UserListItem, event: ChangeEvent<HTMLInputElement>) => void,
  onRemoveUser: (item: UserListItem) => void,
): TableRow[] {
  const classes = styles();
  return data.map((a) => [
    { display: true, value: a.user.username },
    ...generateCheckBoxes(onChangeCan, a),
    {
      display: true,
      value: <DeleteIcon className={classes.deleteIcon} key="1" color="error" onClick={() => onRemoveUser(a)} />,
    },
  ]);
}

export default function NewPermissionGroupPage(): React.ReactElement {
  const classes = styles();
  const [selectedMediaGroups, setSelectedMediaGroups] = useState<MediaGroup[]>([]);
  const [usersList, setUsersList] = useState<UserListItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [createNewPermission] = useMutation<CreatePermissionGroupResponse>(newPermissionGroup);
  const [createMediaPermission] = useMutation(newMediaPermission);
  const navigate = useNavigate();
  const { toastr } = useCustomToast();
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const formik = useFormik<FormValues>({
    initialValues: {
      name: '',
      description: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('This field is required'),
    }),
    onSubmit: async (values: FormValues, submitProps: FormikHelpers<FormValues>) => {
      const { name, description } = values;

      createNewPermission({
        variables: {
          name,
          description,
          mediaGroups: selectedMediaGroups.map(({ id }) => ({ id })),
        },
      })
        .then((res) => {
          if (!res.data) return;

          for (const user of usersList) {
            const {
              canDelete = false,
              canDownload = false,
              canEdit = false,
              canShare = false,
              hires = false,
              id,
            } = user;
            createMediaPermission({
              variables: {
                profileId: id,
                permissionsGroupId: res.data.createPermissionsGroup.permissionsGroup.id,
                canDelete,
                canDownload,
                canEdit,
                canShare,
                hires,
              },
            });
          }
          toastr.success('Permission Group created successfully');
          submitProps.resetForm({ values });
          setSelectedMediaGroups([]);
          setHasSubmitted(true);
        })
        .catch((e: Error) => {
          toastr.error(e.message);
        });
    },
  });

  useEffect(() => {
    if (!formik.dirty && hasSubmitted) {
      navigate({ to: '/systemadmin/groups' });
    }
  }, [formik.dirty, hasSubmitted, navigate]);

  const onAddUsers = (list: Profile[]): void => {
    setUsersList((e) => [...e, ...list.map((p) => ({ ...p }) as UserListItem)]);
  };

  const isUserAlreadyAdded = (profileId: string): boolean => usersList.some((u) => u.id === profileId);

  const onChangeCan = (fieldIndex: number, user: UserListItem, event: ChangeEvent<HTMLInputElement>): void => {
    const _field = headCellsUsersList[fieldIndex + 1].id as keyof UserListItem;
    setUsersList((e) => e.map((v) => (v.id === user.id ? { ...v, [_field]: event.target.checked } : v)));
  };

  const addToSelectedGroups = (e: MediaGroup): void => setSelectedMediaGroups((v) => [...v, e]);
  const removeFromSelectedGroups = (e: MediaGroup): void =>
    setSelectedMediaGroups((v) => v.filter(({ id }) => id !== e.id));

  const onRemoveUser = (item: UserListItem): void => {
    setUsersList((v) => v.filter((e) => e.id !== item.id));
  };

  const onClickCancel = (): void => {
    navigate({ to: '/systemadmin/groups' });
  };

  useEffect(() => {
    if (formik.dirty || selectedMediaGroups.length !== 0) {
      window.onbeforeunload = (): boolean => true;
    } else {
      window.onbeforeunload = null;
    }
  }, [formik.dirty, selectedMediaGroups.length]);

  return (
    <Box sx={{ width: '95%', margin: '0 auto', marginTop: '30px' }}>
      <AddUsersModal
        onAddUsers={onAddUsers}
        isUserAlreadyAdded={isUserAlreadyAdded}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />
      <Card>
        {/* Note: Navigation blocking handled by window.onbeforeunload set in useEffect above */}
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={GRID_CONTAINER_SPACING}>
            <Grid item xs={12}>
              <Title>Create Permission Group</Title>
            </Grid>
          </Grid>
          <Grid container spacing={GRID_CONTAINER_SPACING} style={{ marginBottom: '30px' }}>
            <Grid item xs={6}>
              <div className={classes.cardTitle}>Group Name</div>
              <AdminTextInput label={'Group Name'} {...formik.getFieldProps('name')} />
            </Grid>
            <Grid item xs={6}>
              <div className={classes.cardTitle}>Description</div>
              <AdminTextInput label={'Description'} {...formik.getFieldProps('description')} />
            </Grid>
          </Grid>

          <Grid spacing={GRID_CONTAINER_SPACING} container style={{ marginBottom: '30px' }}>
            <Grid item xs={6}>
              <div className={classes.cardTitle}>Available Media Groups</div>
              <PermissionListSearch onSelect={addToSelectedGroups} selectedPermissions={selectedMediaGroups} />
            </Grid>
            <Grid item xs={6}>
              <div className={classes.cardTitle}>Selected Media Groups</div>
              <SelectedPermission onRemove={removeFromSelectedGroups} selectedPermissions={selectedMediaGroups} />
            </Grid>
          </Grid>

          <Grid
            className={classes.groupUsersContainer}
            container
            spacing={GRID_CONTAINER_SPACING}
            style={{ marginBottom: '30px' }}
          >
            <Grid container>
              <Grid className={classes.cancelSaveButtons} item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={Object.keys(formik.errors).length > 0}
                >
                  Save
                </Button>
                <Button onClick={onClickCancel} color="secondary" style={{ marginLeft: '10px' }}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
            <Grid className={classes.headerContainer} item xs={12}>
              <Title>Group Users</Title>
              <AddButton onClick={() => setIsModalOpen(true)}>Add User</AddButton>
            </Grid>
            <Grid item xs={12}>
              <Table
                minTableHeight={800}
                headCells={headCellsUsersList}
                rows={usersList ? convertUsersRows(usersList, onChangeCan, onRemoveUser) : []}
              />
            </Grid>
          </Grid>
        </form>
      </Card>
    </Box>
  );
}

interface SelectedPermissionProps {
  selectedPermissions: MediaGroup[];
  onRemove: (node: MediaGroup) => void;
}

const SelectedPermission = ({ selectedPermissions, onRemove }: SelectedPermissionProps): React.ReactElement => {
  const classes = styles();
  const theme = useTheme();

  const onClickGroup = (node: MediaGroup): void => {
    onRemove(node);
  };

  const [{ isOver: isOver1 }, dropTarget1] = useDrop({
    accept: 'NEW_PERMISSION_ITEM',
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <div
      ref={(node) => {
        dropTarget1(node);
      }}
      className={classes.permissionContainer}
      style={{ padding: '30px 15px', backgroundColor: isOver1 ? theme.palette.grey[400] : theme.palette.grey[500] }}
    >
      <div className={classes.permissionListContainer}>
        {selectedPermissions.map((a) => (
          <div key={a.id} className={classes.addedContentItem}>
            <div>{a.name}</div>
            <button
              type="button"
              onClick={() => onClickGroup(a)}
              style={{
                marginRight: '10px',
                display: 'flex',
                alignItems: 'center',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
              }}
            >
              <CancelIcon className={classes.cancelIcon} fontSize="small" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

interface PermissionListSearchProps {
  selectedPermissions: MediaGroup[];
  onSelect: (node: MediaGroup) => void;
}

const PermissionListSearch = ({ selectedPermissions, onSelect }: PermissionListSearchProps): React.ReactElement => {
  const classes = styles();
  const [searchVal, setSearchVal] = useState('');

  const { data: groupsList } = useQuery<AllMediaGroupsData>(allMediagroups, {
    fetchPolicy: 'cache-and-network',
  });

  const onChangeSearch = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setSearchVal(e.target.value);
  };

  const handleDrop1 = (node: MediaGroup): void => {
    onSelect(node);
  };

  return (
    <div className={classes.permissionContainer}>
      <div className={classes.permissionSearchBox}>
        <InputBase
          className={classes.searchInput}
          placeholder={'Quick Search'}
          value={searchVal}
          onChange={onChangeSearch}
        />
      </div>
      <div>
        <div className={classes.permissionListContainer}>
          {groupsList?.allMediaGroups.edges
            .filter((a) => !selectedPermissions.find((b) => b.id === a.node.id))
            .filter((a) => a.node.name.toUpperCase().includes(searchVal.toUpperCase()))
            .map((a, index) => (
              <PermissionListSearchItem
                onSelect={onSelect}
                a={a}
                key={a.node.id}
                index={index}
                handleDrop1={handleDrop1}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

interface PermissionListSearchItemProps {
  onSelect: (node: MediaGroup) => void;
  a: MediaGroupNode;
  index: number;
  handleDrop1: (node: MediaGroup) => void;
}

const PermissionListSearchItem = ({
  onSelect,
  a,
  index,
  handleDrop1,
}: PermissionListSearchItemProps): React.ReactElement => {
  const classes = styles();

  const onClickGroup = (node: MediaGroup): void => {
    onSelect(node);
  };

  const id = a.node.id;
  const [{ isDragging: isDragging1 }, dragTarget1] = useDrag({
    type: 'NEW_PERMISSION_ITEM',
    item: { type: 'NEW_PERMISSION_ITEM', id, index },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    end: (_dragItem, monitor) => {
      if (monitor.didDrop()) {
        handleDrop1(a.node);
      }
    },
  });

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Drag-and-drop item also supports click selection; complex interaction pattern
    // biome-ignore lint/a11y/useKeyWithClickEvents: Drag-and-drop item also supports click selection; complex interaction pattern
    <div
      ref={(node) => {
        dragTarget1(node);
      }}
      style={{ opacity: isDragging1 ? 0.5 : 1 }}
      className={classes.permissionListItem}
      onClick={() => onClickGroup(a.node)}
      key={index}
    >
      {a.node.name}
    </div>
  );
};
